'use client';

import { useDialog } from '@musinsa/mds-ui';
import { useEffect, useRef } from 'react';

/**
 * Validation Dialog 케이스 키.
 * - OPTIONS_MISSING : 옵션 미선택
 * - PENALTY        : 페널티 차단
 * - OVER_LIMIT     : 최대 진행 캠페인 초과
 * - SNS_MISSING    : SNS 채널 미등록
 * - ADDRESS_MISSING: 배송지 미등록
 * - AGREEMENT_MISSING: 필수 동의 미완료
 * - CONFIRM        : 신청 확인 (통과 후 최종 확인)
 * - SOLD_OUT       : 수락 시점 상품 품절 (제안형, QAT-39681)
 * - SNS_INELIGIBLE : 등록 SNS 중 콘텐츠 등록 가능 채널 없음 (서버 거절 SEEDING-001-011, QAT-39734)
 */
export type RecruitApplyValidationCaseKey =
  | 'OPTIONS_MISSING'
  | 'PENALTY'
  | 'OVER_LIMIT'
  | 'SNS_MISSING'
  | 'ADDRESS_MISSING'
  | 'AGREEMENT_MISSING'
  | 'CONFIRM'
  | 'SOLD_OUT'
  | 'SNS_INELIGIBLE';

export interface RecruitApplyValidationDialogProps {
  /** null 이면 Dialog 비노출 */
  caseKey: RecruitApplyValidationCaseKey | null;
  /** 패널티 케이스 — BE 응답 동적 사유 문자열 */
  penaltyReason?: string;
  /** SNS 미등록 케이스 — 미등록된 채널명 (예: "Instagram") */
  missingSnsName?: string;
  /** Dialog 닫힘 / 단일 확인 버튼 콜백 */
  onClose: () => void;
  /** dual Dialog 의 secondary(좌측) 콜백 — SNS "다음에 하기" / ADDRESS "취소" */
  onSecondary?: () => void;
  /** dual Dialog 의 primary(우측) "등록하기" 콜백 */
  onRegister?: () => void;
  /** CONFIRM 케이스 — "신청하기" 확인 콜백 */
  onConfirmApply?: () => void;
  /** AGREEMENT_MISSING 메시지의 동작 명사 — 모집형 '신청'(기본) / 제안형 '수락'. */
  actionNoun?: '신청' | '수락';
  /** CONFIRM 케이스 — 선택한 콘텐츠 유형 라벨(예: "이미지"). 동적 안내 문구용. (SID-4369) */
  contentTypeLabel?: string;
  /** CONFIRM 케이스 — 업로드 기한(영업일 N). 동적 안내 문구용. (SID-4369) */
  uploadDeadlineDays?: number;
  /** SOLD_OUT 케이스 — BE 응답 message(본문). 없으면 고정 문구 fallback. (QAT-39681) */
  soldOutMessage?: string;
  /** SNS_INELIGIBLE 케이스 — BE 응답 message(본문). 없으면 고정 문구 fallback. (QAT-39734) */
  noEligibleSnsMessage?: string;
}

interface DialogConfig {
  title: string;
  message?: string;
  confirmButtonText: string;
  cancelButtonText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

function buildDialogConfig(
  props: RecruitApplyValidationDialogProps,
  close: () => void,
): DialogConfig | null {
  const {
    caseKey,
    penaltyReason,
    missingSnsName,
    onClose,
    onSecondary,
    onRegister,
    onConfirmApply,
    actionNoun = '신청',
    contentTypeLabel,
    uploadDeadlineDays,
    soldOutMessage,
    noEligibleSnsMessage,
  } = props;

  if (!caseKey) return null;

  switch (caseKey) {
    case 'OPTIONS_MISSING':
      return {
        title: '상품의 옵션을 선택해 주세요.',
        message: '모든 상품의 옵션을 선택해야 캠페인을 신청할 수 있습니다.',
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };

    case 'PENALTY': {
      const reasonText = penaltyReason ? `페널티 사유: ${penaltyReason}` : undefined;
      return {
        title: '페널티 상태로 신규 신청이 제한되었어요.',
        message: reasonText,
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };
    }

    case 'OVER_LIMIT':
      return {
        title: '진행 중인 캠페인을 확인해 주세요.',
        message: '한 번에 진행 가능한 캠페인은 10개입니다. 먼저 진행 중인 캠페인을 완료해 주세요.',
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };

    case 'SNS_MISSING': {
      const snsLabel = missingSnsName ?? 'SNS';
      return {
        title: `${snsLabel} 채널 등록이 필요합니다.`,
        message: `해당 캠페인을 진행하려면 ${snsLabel} 채널 등록이 필요합니다. 채널을 등록하시겠습니까?`,
        cancelButtonText: '다음에 하기',
        confirmButtonText: '등록하기',
        onCancel: () => {
          close();
          onSecondary?.();
        },
        onConfirm: () => {
          close();
          onRegister?.();
        },
      };
    }

    case 'ADDRESS_MISSING':
      return {
        title: '배송지 등록이 필요합니다.',
        cancelButtonText: '취소',
        confirmButtonText: '등록하기',
        onCancel: () => {
          close();
          onSecondary?.();
        },
        onConfirm: () => {
          close();
          onRegister?.();
        },
      };

    case 'AGREEMENT_MISSING':
      return {
        title: '필수 동의 항목을 확인해 주세요.',
        message: `모든 필수 항목에 동의하셔야 캠페인을 ${actionNoun}할 수 있습니다.`,
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };

    case 'SOLD_OUT':
      // 제목은 코드 매핑으로 고정, 본문은 BE 응답 message 우선(없으면 고정 fallback). (QAT-39681)
      return {
        title: '상품 품절로 캠페인 진행이 불가합니다.',
        message:
          soldOutMessage ??
          '캠페인 제안 이후 상품이 품절되어 캠페인 진행이 불가합니다. 너른 양해 부탁드립니다.',
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };

    case 'SNS_INELIGIBLE':
      // 등록 SNS 중 콘텐츠 등록 가능 채널 없음 — 등록 유도가 아닌 단일 확인. 본문은 BE message 우선. (QAT-39734)
      return {
        title: 'SNS 채널을 확인해 주세요.',
        message:
          noEligibleSnsMessage ?? '등록된 SNS 채널 중 캠페인 콘텐츠 등록 가능 채널이 없습니다.',
        confirmButtonText: '확인',
        onConfirm: () => {
          close();
          onClose();
        },
      };

    case 'CONFIRM': {
      // 문구·버튼 라벨은 Figma 기준 — 신청(node 1-23466, SID-4089) / 수락(node 1-24791, SID-4369).
      // 콘텐츠 유형·업로드 기한(N일)은 선택한 캠페인 값으로 동적 조립. (SID-4369)
      // 값 누락 시 고정 수치 없는 일반 문구로 fallback (잘못된 7일/사진 하드코딩 방지).
      const uploadNotice =
        uploadDeadlineDays != null && contentTypeLabel
          ? `배송 완료일로부터 ${uploadDeadlineDays}일 이내 ${contentTypeLabel} 콘텐츠를 등록하고 링크를 공유해야 합니다.`
          : '배송 완료일로부터 기한 내 콘텐츠를 등록하고 링크를 공유해야 합니다.';
      const onConfirmCommon = {
        onCancel: () => {
          close();
          onClose();
        },
        onConfirm: () => {
          close();
          onConfirmApply?.();
        },
      };
      if (actionNoun === '수락') {
        return {
          title: '캠페인을 수락하시겠습니까?',
          message: `수락하면 영업일 3일 이내 상품이 발송됩니다. ${uploadNotice}`,
          cancelButtonText: '취소',
          confirmButtonText: '확인',
          ...onConfirmCommon,
        };
      }
      return {
        title: '캠페인을 신청하시겠어요?',
        message: `선정되면 취소할 수 없으며 영업일 3일 이내 상품이 발송됩니다. ${uploadNotice}`,
        cancelButtonText: '다음에 하기',
        confirmButtonText: '신청하기',
        ...onConfirmCommon,
      };
    }

    default:
      return null;
  }
}

/**
 * 모집형 신청 validation Dialog — 5 케이스 + CONFIRM 분기를 단일 컴포넌트로 처리.
 *
 * `caseKey` 가 변경될 때마다 해당 케이스에 맞는 Dialog 를 자동으로 열고 닫는다.
 * `useDialog` (mds-ui) 의 전역 Dialog Provider 를 사용.
 *
 * @see plan apply-validation-plan.md §1-1 / §4
 */
export function RecruitApplyValidationDialog(props: RecruitApplyValidationDialogProps) {
  const { caseKey } = props;
  const dialog = useDialog();
  // 현재 열린 caseKey 추적 — 동일 key 중복 open 방지
  const openedKeyRef = useRef<RecruitApplyValidationCaseKey | null>(null);

  useEffect(() => {
    if (!caseKey) {
      if (openedKeyRef.current !== null) {
        dialog.close();
        openedKeyRef.current = null;
      }
      return;
    }

    // 동일 케이스 중복 open 방지
    if (openedKeyRef.current === caseKey) return;

    const config = buildDialogConfig(props, dialog.close);
    if (!config) return;

    openedKeyRef.current = caseKey;
    dialog.open({
      title: config.title,
      message: config.message,
      confirmButtonText: config.confirmButtonText,
      cancelButtonText: config.cancelButtonText,
      onConfirm: config.onConfirm,
      onCancel: config.onCancel,
    });
    // NOTE: props 전체를 deps 에 포함하면 무한 루프 위험 — caseKey 변경만 감지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseKey]);

  // 페이지 이탈(브라우저 뒤로가기 등)로 컴포넌트가 unmount 될 때, 전역 Dialog provider 가 띄운 팝업이
  // 잔존하지 않도록 함께 닫는다. (QAT-39694) caseKey effect 와 별개의 unmount 전용 cleanup.
  // dialog.close 는 provider 의 useCallback 안정 참조라 mount 1회 등록 → unmount 에서만 발화.
  useEffect(() => {
    return () => {
      if (openedKeyRef.current !== null) dialog.close();
    };
  }, [dialog.close]);

  return null;
}
